
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  AlertCircle, Check,
  Package, AlertTriangle, TrendingUp, BarChart3, ArrowRight
} from '../components/Icons';
import { Card, PageHeader } from '../components/UI';
import { inventoryData } from '../data/inventory';
import { useTranslation } from '../contexts/LanguageContext';
import { StockStatus } from '../types';

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Statistics
  const stats = {
    total: inventoryData.length,
    critical: inventoryData.filter(i => i.status === StockStatus.CRITICAL).length,
    healthy: inventoryData.filter(i => i.status === StockStatus.HEALTHY).length,
    warning: inventoryData.filter(i => i.status === StockStatus.WARNING).length,
  };

  // Analytics for Charts
  const categoryStats = useMemo(() => {
    const acc: Record<string, number> = {};
    inventoryData.forEach(item => {
        acc[item.category] = (acc[item.category] || 0) + 1;
    });
    return Object.entries(acc).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count);
  }, []);

  const maxCategoryCount = Math.max(...categoryStats.map(c => c.count));

  return (
    <div className="p-6 space-y-6 max-w-[1920px] mx-auto animate-in fade-in duration-500">
      <PageHeader 
        title={t('dashboard')} 
        breadcrumbs={[{ label: 'VMI Hub' }, { label: t('dashboard') }]}
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="p-6 flex items-center justify-between border-l-4 border-l-brand-dark">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">{t('total_products')}</p>
            <h3 className="text-3xl font-black text-brand-dark dark:text-white mt-1">{stats.total}</h3>
          </div>
          <div className="w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center text-gray-600 dark:text-gray-300">
            <Package size={24} />
          </div>
        </Card>

        <Card className="p-6 flex items-center justify-between border-l-4 border-l-red-500">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">{t('critical_items')}</p>
            <h3 className="text-3xl font-black text-red-600 dark:text-red-400 mt-1">{stats.critical}</h3>
          </div>
          <div className="w-12 h-12 bg-red-50 dark:bg-red-900/20 rounded-full flex items-center justify-center text-red-600 dark:text-red-400">
            <AlertCircle size={24} />
          </div>
        </Card>

        <Card className="p-6 flex items-center justify-between border-l-4 border-l-green-500">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">{t('healthy_items')}</p>
            <h3 className="text-3xl font-black text-green-600 dark:text-green-400 mt-1">{stats.healthy}</h3>
          </div>
          <div className="w-12 h-12 bg-green-50 dark:bg-green-900/20 rounded-full flex items-center justify-center text-green-600 dark:text-green-400">
            <Check size={24} />
          </div>
        </Card>

        <Card className="p-6 flex items-center justify-between border-l-4 border-l-amber-500">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">{t('warning')}</p>
            <h3 className="text-3xl font-black text-amber-600 dark:text-amber-400 mt-1">{stats.warning}</h3>
          </div>
          <div className="w-12 h-12 bg-amber-50 dark:bg-amber-900/20 rounded-full flex items-center justify-center text-amber-600 dark:text-amber-400">
            <AlertTriangle size={24} />
          </div>
        </Card>
      </div>

      {/* Analytics Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
         <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
               <div className="flex items-center gap-3">
                  <BarChart3 size={20} className="text-brand-medium" />
                  <h3 className="text-sm font-bold uppercase tracking-widest text-brand-dark dark:text-white">{t('inventory_by_category')}</h3>
               </div>
            </div>
            <div className="space-y-4">
                {categoryStats.map(cat => (
                    <div key={cat.name} className="flex items-center gap-4">
                        <div className="w-24 text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 truncate">{cat.name}</div>
                        <div className="flex-1 h-2 bg-gray-100 dark:bg-[#262626] rounded-full overflow-hidden">
                            <div className="h-full bg-brand-medium" style={{ width: `${(cat.count / maxCategoryCount) * 100}%` }}></div>
                        </div>
                        <div className="w-8 text-right text-xs font-bold text-brand-dark dark:text-white">{cat.count}</div>
                    </div>
                ))}
            </div>
         </Card>

         <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
               <div className="flex items-center gap-3">
                  <TrendingUp size={20} className="text-brand-medium" />
                  <h3 className="text-sm font-bold uppercase tracking-widest text-brand-dark dark:text-white">{t('stock_health_overview')}</h3>
               </div>
            </div>
            <div className="flex h-32 gap-1 rounded-md overflow-hidden my-6">
               <div style={{ width: `${(stats.healthy / stats.total) * 100}%` }} className="bg-green-500 hover:opacity-90 transition-opacity relative group">
                  <div className="absolute inset-0 flex items-center justify-center text-white font-bold text-lg">{Math.round((stats.healthy / stats.total) * 100)}%</div>
                  <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase tracking-widest text-gray-500">{t('healthy')}</div>
               </div>
               <div style={{ width: `${(stats.warning / stats.total) * 100}%` }} className="bg-amber-500 hover:opacity-90 transition-opacity relative group">
                  <div className="absolute inset-0 flex items-center justify-center text-white font-bold text-lg">{Math.round((stats.warning / stats.total) * 100)}%</div>
                  <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase tracking-widest text-gray-500">{t('warning')}</div>
               </div>
               <div style={{ width: `${(stats.critical / stats.total) * 100}%` }} className="bg-red-500 hover:opacity-90 transition-opacity relative group">
                  <div className="absolute inset-0 flex items-center justify-center text-white font-bold text-lg">{Math.round((stats.critical / stats.total) * 100)}%</div>
                  <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase tracking-widest text-gray-500">{t('critical')}</div>
               </div>
            </div>
            <p className="text-center text-xs text-gray-400 mt-10">
               {t('current_distribution')}
            </p>
         </Card>
      </div>

      {/* CTA Block to Stock List */}
      <Card className="p-8 border-dashed border-2 border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-[#111111]">
         <div className="flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
            <div>
               <h3 className="text-lg font-bold text-brand-dark dark:text-white mb-2">{t('detailed_stock_mgmt')}</h3>
               <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xl">
                  {t('detailed_stock_desc')}
               </p>
            </div>
            <button 
               onClick={() => navigate('/stock')}
               className="group flex items-center gap-3 bg-brand-dark text-white px-8 py-3 rounded-[6px] text-xs font-bold uppercase tracking-widest hover:bg-brand-medium transition-all shadow-md"
            >
               {t('go_to_stock_list')} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
         </div>
      </Card>
    </div>
  );
};

export default Dashboard;
